import React, { useContext, useState, useRef, useEffect } from 'react';
import { Configure } from 'react-instantsearch-dom';

// Components
import SearchAttendeeListLastPage from '../../elements/SearchAttendeeListLastPage';
import ErrorHandler from '../../sections/ErrorHandler';
import FiltersOverlay from '../../sections/FiltersOverlay';
import FiltersSidebar from '../../sections/FiltersSidebar';
import Hero from '../../sections/Hero';
import PagingToolbar from '../../sections/PagingToolbar';
import SearchOptionsDesktop from '../../sections/SearchOptionsDesktop';
import SearchOptionsMobile from '../../sections/SearchOptionsMobile';
import SearchResults from '../../sections/SearchResults';
import FixedFeedbackButton from '../../sections/FixedFeedbackButton';
import LoadingArea from '@molecules/LoadingArea';
import Unauthorized from '@molecules/Unauthorized';
import SearchContainer from '@search/SearchContainer';

// Functionality
import { searchStateToUrl, urlToSearchState } from '../../../helpers/search';
import { SearchTypeContext } from '../../../context/searchTypeContext';
import {
    SearchModeContext,
    ConfigFilterContext,
    PageSizeContext,
    SortByContext,
    SearchStateContext,
} from '../../../context/searchContext';

import { ViewTypeContext } from '@context/viewTypeContext';
import { useScreenMinLg, useScreenMdDown } from '@hooks/useResponsive';
import useAuthorized from '@hooks/useAuthorized';
import useViewer from '@hooks/useViewer';

const DEBOUNCE_TIME = 400;

const SearchPage = () => {
    // -----------------------------
    // State
    // -----------------------------

    const [analyticsTags, setAnalyticsTags] = useState([]);
    const [searchState, setSearchState] = useState(urlToSearchState(window.location));
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [debouncedSetState, setDebouncedSetState] = useState(null);
    const [isAttendeeSearch, setIsAttendeeSearch] = useState(null);
    const [canViewAttendees, setCanViewAttendees] = useState(null);

    const [searchType] = useContext(SearchTypeContext);
    const [pageSize] = useContext(PageSizeContext);
    const [sortBy, setSortBy, setSortByDefault] = useContext(SortByContext);
    const [viewType] = useContext(ViewTypeContext);
    const { configFilter, includePastEvents, setConfigFilterForSearchType } = useContext(ConfigFilterContext);
    const { eventCode, showFilters } = useContext(SearchModeContext);

    const containerDesktop = useRef(null);
    const containerMobile = useRef(null);
    const { isMemberActive } = useAuthorized();
    const { viewer } = useViewer();

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (eventCode) {
            setAnalyticsTags([`attendee:${eventCode}`, 'attendee']);
            setIsAttendeeSearch(true);
        } else {
            setIsAttendeeSearch(false);
        }
    }, [eventCode]);

    useEffect(() => {
        if (!eventCode || !viewer) {
            return;
        }

        if (
            viewer.events
                .filter(event => !!event)
                .map(event => event.code)
                .includes(eventCode)
        ) {
            setCanViewAttendees(true);
        } else {
            setCanViewAttendees(false);
        }
    }, [eventCode, viewer]);

    // -----------------------------
    // Helpers
    // -----------------------------

    const onSearchStateChange = ({
        searchTypeIndex,
        sortByQueryParam,
        pageSizeHitsPerPage,
        viewType: updatedViewType,
        searchState: updatedSearchState,
    }) => {
        clearTimeout(debouncedSetState);
        setDebouncedSetState(
            setTimeout(() => {
                const url = searchStateToUrl({
                    searchType: searchTypeIndex,
                    sortBy: sortByQueryParam,
                    pageSize: pageSizeHitsPerPage,
                    viewType: updatedViewType,
                    searchState: updatedSearchState,
                    includePastEvents,
                });
                window.history.pushState('', '', url);

                if (window.ga) {
                    window.ga('send', 'pageView', url);
                }
            }, DEBOUNCE_TIME)
        );

        setSearchState(updatedSearchState);
    };

    const onSearchTypeChange = selectedSearchType => {
        setConfigFilterForSearchType({ newSearchType: selectedSearchType, eventCode });

        const updatedSearchState = {};
        if (searchState.query) {
            updatedSearchState.query = searchState.query;
        }

        onSearchStateChange({
            searchTypeIndex: selectedSearchType.index,
            sortByQueryParam: '',
            pageSizeHitsPerPage: pageSize.hitsPerPage,
            viewType,
            searchState: updatedSearchState,
        });
        setSortByDefault(selectedSearchType.index);
    };

    // -----------------------------
    // Render
    // -----------------------------

    const isTabletDown = useScreenMdDown();
    const isDesktop = useScreenMinLg();

    if (filterModalOpen) {
        document.body.style.position = 'fixed';
    } else {
        document.body.style.position = 'static';
    }

    const renderDesktop = () => {
        if (isAttendeeSearch === false && isMemberActive === false && searchType.authRequired) {
            return <Unauthorized text="You must have an active membership to view this content." />;
        }

        if (isAttendeeSearch && !isMemberActive && canViewAttendees === false) {
            return <Unauthorized text="You must be registered for this event to view the attendee list." />;
        }

        return (
            <div className="container-fluid px-0" style={{ maxWidth: showFilters ? '1600px' : '1200px' }}>
                <div className="d-flex" ref={containerDesktop}>
                    {showFilters && (
                        <div className="w-25" style={styles.desktopSidebar}>
                            <FiltersSidebar />
                        </div>
                    )}

                    <div className={`px-5 ${showFilters ? 'w-75' : 'w-100 mx-auto'}`}>
                        <div className="pt-md-4 pb-md-5">
                            <SearchOptionsDesktop
                                pageSize={pageSize}
                                onPageSizeChange={selectedPageSize => {
                                    onSearchStateChange({
                                        searchTypeIndex: searchType.index,
                                        sortByQueryParam: sortBy.queryParam,
                                        pageSizeHitsPerPage: selectedPageSize.hitsPerPage,
                                        viewType,
                                        searchState,
                                    });
                                }}
                                onSortByChange={selectedSortBy => {
                                    onSearchStateChange({
                                        searchTypeIndex: searchType.index,
                                        sortByQueryParam: selectedSortBy.queryParam,
                                        pageSizeHitsPerPage: pageSize.hitsPerPage,
                                        viewType,
                                        searchState,
                                    });
                                }}
                                onViewTypeChange={selectedViewType => {
                                    onSearchStateChange({
                                        searchTypeIndex: searchType.index,
                                        sortByQueryParam: sortBy.queryParam,
                                        pageSizeHitsPerPage: pageSize.hitsPerPage,
                                        viewType: selectedViewType,
                                        searchState,
                                    });
                                }}
                            />
                        </div>

                        <SearchResults />
                        <SearchAttendeeListLastPage />
                        <div className="pt-4 pb-5">
                            <PagingToolbar scrollToRef={containerDesktop} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderMobile = () => {
        if (isAttendeeSearch === false && !isMemberActive && searchType.authRequired) {
            return <Unauthorized text="You must have an active membership to view this content." />;
        }

        if (isAttendeeSearch && !isMemberActive && canViewAttendees === false) {
            return <Unauthorized text="You must be registered for this event to view the attendee list." />;
        }

        return (
            <>
                <div ref={containerMobile} style={styles.mobileBody}>
                    <div className="mb-3">
                        <SearchOptionsMobile
                            onFilterButtonClick={() => setFilterModalOpen(true)}
                            onPageSizeChange={selectedPageSize => {
                                onSearchStateChange({
                                    searchTypeIndex: searchType.index,
                                    sortByQueryParam: sortBy.queryParam,
                                    pageSizeHitsPerPage: selectedPageSize.hitsPerPage,
                                    viewType,
                                    searchState,
                                });
                            }}
                            onSortByChange={selectedSortBy => {
                                onSearchStateChange({
                                    searchTypeIndex: searchType.index,
                                    sortByQueryParam: selectedSortBy.queryParam,
                                    pageSizeHitsPerPage: pageSize.hitsPerPage,
                                    viewType,
                                    searchState,
                                });
                            }}
                        />
                    </div>

                    <SearchResults />
                    <div className="pt-4 pb-5">
                        <PagingToolbar scrollToRef={containerMobile} />
                    </div>
                </div>
                {showFilters && <FiltersOverlay open={filterModalOpen} onClose={() => setFilterModalOpen(false)} />}
            </>
        );
    };

    if (isAttendeeSearch && canViewAttendees === null) {
        return <LoadingArea />;
    }

    if (isAttendeeSearch === null || isMemberActive === null) {
        return <LoadingArea />;
    }

    return (
        <ErrorHandler>
            <SearchContainer
                key={searchType.index}
                index={searchType.index}
                searchState={searchState}
                onSearchStateChange={updatedSearchState =>
                    onSearchStateChange({
                        searchTypeIndex: searchType.index,
                        sortByQueryParam: sortBy.queryParam,
                        pageSizeHitsPerPage: pageSize.hitsPerPage,
                        viewType,
                        searchState: updatedSearchState,
                    })
                }
            >
                <Configure
                    clickAnalytics
                    maxValuesPerFacet={100}
                    facets={searchType.facets || []}
                    filters={configFilter}
                    analyticsTags={analyticsTags}
                />

                <Hero onSearchTypeChange={onSearchTypeChange} />

                <SearchStateContext.Provider value={[searchState]}>
                    {isDesktop && renderDesktop()}
                    {isTabletDown && renderMobile()}
                </SearchStateContext.Provider>
            </SearchContainer>
        </ErrorHandler>
    );
};

const styles = {
    desktopSidebar: {
        backgroundColor: '#f9f9f9',
    },
    mobileBody: {
        paddingLeft: '1.75rem',
        paddingRight: '1.75rem',
    },
};

SearchPage.propTypes = {};

export default SearchPage;
